import { useMemo } from 'react';
import { useQuery } from '@apollo/client';

import { getVendorMenuTemplate } from '@/api';
import { resolveGqlErrors } from '@/helpers';

import { MENU_ITEMS_PER_PAGE } from '../constants';
import type { MenuItem, MenuTemplateDetail } from '../types';

type UseVendorMenuTemplate = {
  template: MenuTemplateDetail | null;
  items: MenuItem[];
  totalCount: number;
  pageCount: number;
  loading: boolean;
  error: string;
};

export const useVendorMenuTemplate = (
  templateId: string | null,
  page: number
): UseVendorMenuTemplate => {
  const { data, loading, error } = useQuery(getVendorMenuTemplate, {
    variables: {
      input: { id: templateId ?? '' },
      first: MENU_ITEMS_PER_PAGE,
      skip: (page - 1) * MENU_ITEMS_PER_PAGE,
    },
    skip: !templateId,
    fetchPolicy: 'cache-first',
  });

  const template = (data?.getVendorMenuTemplate?.menuTemplate ?? null) as MenuTemplateDetail | null;
  const connection = data?.getVendorMenuTemplate?.menuTemplate?.items ?? null;

  const items = useMemo<MenuItem[]>(
    () => (connection?.nodes ?? []).filter((node): node is MenuItem => node !== null),
    [connection]
  );

  const totalCount = connection?.totalCount ?? 0;
  const pageCount = Math.max(1, Math.ceil(totalCount / MENU_ITEMS_PER_PAGE));

  return {
    template,
    items,
    totalCount,
    pageCount,
    loading,
    error: resolveGqlErrors(error || data?.getVendorMenuTemplate?.error),
  };
};
